import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiRefresh } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  ChatbotRTKService,
  useAddFileKbAssistantBotMutation,
  useCreateAssistantBotMutation,
  useCreateKbAssistantBotMutation,
  useDeleteFileKbAssistantBotMutation,
  useEditAssistantBotMutation,
  useEditKbAssistantBotMutation,
  useGetAssistantsQuery,
  useLazyGetAllScenariosQuery,
  useRescanBotMutation,
  useSetDefaultScenariosMutation
} from "../../../app/services/chatbot-rtk.services";
import {
  useCreateNewScenarioMutation,
  useModifyScenarioMutation
} from "../../../app/services/scenario-rtk.service";
import { store } from "../../../app/store";
import { AlertsType } from "../../../constants/alerts.constant";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import { handleInitializeEditAppearance } from "../../../features/appearance/appearanceSlice";
import AiAdditionalInfoEdit from "../../../features/bot/AiAdditionalInfoEdit";
import {
  assistantSelectors,
  handleInitializeEditWizard,
  handleResetWizard,
  handleSetCloneExtra,
  handleSetCloneTemplate,
  wizardState
} from "../../../features/bot/botSlice";
import CloneExtraDescriptionEdit from "../../../features/bot/CloneExtraDescriptionEdit";
import CloneExtraVideoEdit from "../../../features/bot/CloneExtraVideoEdit";
import PlugAndPlayAutoUpdateEdit from "../../../features/bot/PlugAndPlayAutoUpdateEdit";
import PlugAndPlayDomainEdit from "../../../features/bot/PlugAndPlayDomainEdit";
import PlugAndPlayDomainLinksEdit from "../../../features/bot/PlugAndPlayDomainLinksEdit";
import ReactionSpeedEdit from "../../../features/bot/ReactionSpeedEdit";
import {
  AIKittyType,
  BotCode,
  McBotFaceType
} from "../../../features/bot/types";
import { setCurrentScenario } from "../../../features/scenario.slice";
import { ChatbotEditContext } from "../../../pages/dashboard/ChatbotEdit";
import { BotCategoryEnum } from "../../../utils/BotCategoryEnum";
import { BotTypeEnum } from "../../../utils/BotTypeEnum";
import LoadingComponent from "../../common/loading/loading.component";
import LouiseButton from "../../ui/LouiseButton";
import BotEditAgentWelcomingName from "./components/bot-edit-agent-welcoming-name";
import BotEditChatbotName from "./components/bot-edit-chatbot-name";
import BotSessionLimit from "./components/bot-sessionLimit";

function BotEditConfiguration() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ChatbotEditContextValue = useContext(ChatbotEditContext);
  const goToTitle = () => {
    if (ChatbotEditContextValue?.value.botId)
      navigate(
        `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
          ChatbotEditContextValue?.value.botId
        }&tab=config`
      );
  };

  const wizard = useAppSelector(wizardState);

  const [isLoading, setIsLoading] = useState(false);

  const targetAssistantData = assistantSelectors.selectById(
    store.getState(),
    (wizard?.bot_config as AIKittyType)?.assistant
  );

  const [clonedData, setClonedData] = useState<any | null>(null);
  const [wizardStep, setWizardStep] = useState(
    wizard.bot_ui_info.currentStep || 1
  );

  // Initialize required data for clone (We have omitted required step in wizard so we have to insure that additional_instructions are prepared
  const { isError: isGetAssistantError, refetch: getAssistantRefetch } =
    useGetAssistantsQuery(
      {
        language: i18n.language
      },
      { skip: wizard.bot_code !== BotTypeEnum.HUMAN_CLONE }
    );

  // Getting all bots to check for their limit
  const [getAllBots] = useLazyGetAllScenariosQuery();

  // McBotType Mutators
  const [createMcBot, createMcBotResults] = useCreateNewScenarioMutation();
  const [editMcBot, editMcBotResults] = useModifyScenarioMutation();

  // AiKittyType Mutators
  const [createAiKittyBot, createAiKittyBotResults] =
    useCreateAssistantBotMutation();
  const [editAiKittyBot, editAiKittyBotResults] = useEditAssistantBotMutation();

  const [createKbAiKittyBot, createKbAiKittyBotResults] =
    useCreateKbAssistantBotMutation();
  const [editKbAiKittyBot, editKbAiKittyBotResults] =
    useEditKbAssistantBotMutation();
  const [, addFileKbAiKittyBotResults] = useAddFileKbAssistantBotMutation({
    fixedCacheKey: "aiKnowledgeFileMutator"
  });
  const [, removeFileKbAiKittyBotResults] = useDeleteFileKbAssistantBotMutation(
    { fixedCacheKey: "aiKnowledgeFileDeleteMutator" }
  );
  const [reScanBot, reScanotResults] = useRescanBotMutation();

  // Make default Mutator
  const [makeBotDefault, makeBotDefaultResults] =
    useSetDefaultScenariosMutation();

  const isApiInLoadingStatus =
    createMcBotResults.isLoading ||
    editMcBotResults.isLoading ||
    createAiKittyBotResults.isLoading ||
    editAiKittyBotResults.isLoading ||
    createKbAiKittyBotResults.isLoading ||
    editKbAiKittyBotResults.isLoading ||
    addFileKbAiKittyBotResults.isLoading ||
    removeFileKbAiKittyBotResults.isLoading ||
    makeBotDefaultResults.isLoading;

  const processExtraRequiredKeys = (data: any[] | undefined) => {
    const labelMapper: Record<string, string> = {
      company_name: t("Wizard/validationLabels/key8"),
      agent_name: t("Wizard/validationLabels/key9"),
      domain: t("Wizard/validationLabels/key10"),
      personality: t("Wizard/validationLabels/key11"),
      legal_sector: t("Wizard/validationLabels/key12"),
      business_sector: t("Wizard/validationLabels/key13")
    };

    if (data && data.length > 0) {
      const validationKeys: any = {};
      for (const obj of data) {
        if (obj.name == "agent_name") {
          validationKeys[obj.name] = Yup.string()
            .max(30, "Max length should be 30")
            .required()
            .label(labelMapper[obj.name]);
        } else {
          validationKeys[obj.name] = Yup.string()
            .required()
            .label(labelMapper[obj.name]); // Initialize or append
        }
      }
      return validationKeys;
    }
  };

  const cloneValidationSchema = () => {
    switch ((wizard.bot_config as AIKittyType)?.cloneExtra?.template) {
      case "basic":
        return {
          model: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key5")),
          training_data: Yup.array(
            Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
          ).optional()
        };
      case "clonePlusDescription":
        return {
          name: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key18")),
          profession: Yup.string().label(t("Wizard/validationLabels/key19")),
          bio: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key20")),
          externalLinks: Yup.array(
            Yup.object({
              logo: Yup.string()
                .required()
                .label(t("Wizard/validationLabels/key21")),
              link: Yup.string()
                .required()
                .label(t("Wizard/validationLabels/key22"))
            }).required()
          ),
          model: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key5")),
          training_data: Yup.array(
            Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
          ).optional()
        };
      case "clonePlusVideo":
        return {
          videoBotId: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key23")),
          externalLinks: Yup.array(
            Yup.object({
              logo: Yup.string()
                .required()
                .label(t("Wizard/validationLabels/key21")),
              link: Yup.string()
                .required()
                .label(t("Wizard/validationLabels/key22"))
            }).required()
          ),
          model: Yup.string()
            .required()
            .label(t("Wizard/validationLabels/key5")),
          training_data: Yup.array(
            Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
          ).optional()
        };
      default:
        return;
    }
  };

  const baseValidationSchema = () => {
    return {
      title: Yup.string()
        .required(
          t("Wizard/validationLabels/key15", {
            variable: t("Wizard/step1/key7")
          })
        )
        .min(1)
        .max(30, t("Wizard/validationLabels/key16"))
    };
  };

  const validationSchema: {
    [key: string]: any;
  } = {
    KITTY_02: {
      ...baseValidationSchema(),
      assistant_type: Yup.string()
        .required()
        .label(t("Wizard/validationLabels/key1")),
      additional_instructions: Yup.string()
        .optional()
        .label(t("Wizard/validationLabels/key2")),
      welcoming: Yup.string()
        .optional()
        .label(t("Wizard/validationLabels/key3")),
      ...processExtraRequiredKeys(targetAssistantData?.required_instructions),
      model: Yup.string().required().label(t("Wizard/validationLabels/key5"))
    },
    KITTY_03: {
      ...baseValidationSchema(),
      assistant_type: Yup.string()
        .required()
        .label(t("Wizard/validationLabels/key1")),
      additional_instructions: Yup.string()
        .optional()
        .label(t("Wizard/validationLabels/key2")),
      welcoming: Yup.string().optional(),
      ...processExtraRequiredKeys(targetAssistantData?.required_instructions),
      training_data: Yup.array(
        Yup.mixed().required().label(t("Wizard/validationLabels/key4"))
      ).optional(),
      model: Yup.string().required().label(t("Wizard/validationLabels/key5"))
    },
    KITTY_04: {
      ...baseValidationSchema(),
      address: Yup.string()
        .matches(
          /^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/i,
          {
            message: t("DashboardSettingWidgetDefaultDomain/er-01")
          }
        )
        .required(),
      isVerified: Yup.bool()
        .oneOf([true], t("Wizard/PlugAndPlayInfos/key19"))
        .required(t("Wizard/PlugAndPlayInfos/key19")),
      links: Yup.array()
        .of(Yup.string().url().required())
        .min(1)
        .max(20)
        .required(),
      auto_update: Yup.string()
        .required()
        .label(t("Wizard/validationLabels/key17")),
      welcoming: Yup.string()
        .optional()
        .label(t("Wizard/validationLabels/key3")),
      agent_name: Yup.string()
        .required()
        .label(t("Wizard/validationLabels/key9"))
    },
    MCBOT_01: {
      ...baseValidationSchema(),
      template_id: Yup.number()
        .required()
        .label(t("Wizard/validationLabels/key6"))
    },
    VIDEO_SCENARIO: {
      ...baseValidationSchema(),
      template_id: Yup.number()
        .required()
        .label(t("Wizard/validationLabels/key6"))
    },
    CLONE_01: {
      ...baseValidationSchema(),
      welcoming: Yup.string()
        .optional()
        .label(t("Wizard/validationLabels/key3")),
      ...processExtraRequiredKeys(targetAssistantData?.required_instructions),
      ...cloneValidationSchema()
    }
  };

  const currentValidationSchema = wizard.bot_code
    ? Yup.object({ ...validationSchema[wizard.bot_code] })
    : null;

  const methods = useForm({
    shouldUnregister: false,
    resolver: currentValidationSchema
      ? yupResolver(currentValidationSchema)
      : undefined,
    mode: "all"
  });

  const formBuilder = methods;

  const isChange = (title: any): boolean => {
    if (wizard.bot_code == BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) {
      if (
        JSON.stringify((wizard.bot_config as any).plug_and_play) ===
          JSON.stringify(clonedData?.bot_config.plug_and_play) &&
        title == ChatbotEditContextValue?.value?.title
      ) {
        return true;
      }
    }

    if (
      JSON.stringify(wizard?.bot_config) ===
        JSON.stringify(clonedData?.bot_config) &&
      title == ChatbotEditContextValue?.value?.title
    ) {
      // Not sure maybe its better to show toas for real update??
      return true;
    }

    return false;
  };

  /* Although data has all required data,
   * but in order to prevent multiple data source we will rely on state data
   */
  const onSubmit = (data: any) => {
    // Case for when data is not modified in order to prevent api call

    if (
      wizard.bot_code !== BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY &&
      isChange(data.title)
    ) {
      toast.success(t(AlertsType.ISKBKO004));
      return;
    }
    if (wizard.bot_config)
      if (wizard.bot_type === BotCategoryEnum.SCENARIO_BASED_CHATBOT) {
        if (wizard.isInEditMode) {
          editMcBot({
            scenarioId: wizard.id,
            data: {
              title: data.title
            }
          })
            .unwrap()
            .then(() => {
              toast.success(t(AlertsType.ISKBKO004));

              dispatch(setCurrentScenario(wizard.id));

              dispatch(
                ChatbotRTKService.util.invalidateTags([
                  "Package",
                  "GetAllScenarios",
                  "GetAssistantDetail"
                ])
              );

              goToTitle();
            });
        }
      } else {
        if (wizard.isInEditMode) {
          wizard.bot_code === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
          wizard.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY ||
          wizard.bot_code === BotTypeEnum.HUMAN_CLONE // if Ai is Knowledge then we have to manage files too
            ? editKbAiKittyBot(
                wizard.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY
                  ? {
                      id: wizard.id,
                      ...(wizard.bot_config as AIKittyType),
                      title: data.title,
                      welcoming: data?.welcoming,
                      instructions: {
                        ...(wizard.bot_config as AIKittyType).instructions,
                        agent_name: data?.agent_name
                      },
                      model: (wizard.bot_config as AIKittyType).modelTitle
                    }
                  : {
                      id: wizard.id,
                      ...(wizard.bot_config as AIKittyType),
                      title: data.title,
                      model: (wizard.bot_config as AIKittyType).modelTitle
                    }
              )
                .unwrap()
                .then((value) => {
                  toast.success(t(AlertsType.ISKBKO004));
                  dispatch(
                    ChatbotRTKService.util.invalidateTags([
                      "Package",
                      "GetAllScenarios",
                      "GetAssistantDetail"
                    ])
                  );
                  goToTitle();
                  setClonedData(wizard);
                  dispatch(
                    handleInitializeEditWizard({
                      title: value.title,
                      id: value.id,
                      bot_code: value.type as BotCode,
                      bot_type:
                        (value.type as BotCode) ===
                          BotTypeEnum.VIDEO_SCENARIO ||
                        (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                          ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                          : BotCategoryEnum.AI_BASED_CHATBOT,
                      bot_config:
                        (value.type as BotCode) ===
                          BotTypeEnum.VIDEO_SCENARIO ||
                        (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                          ? ({
                              bot_code: value.type,
                              title: data.title,
                              template_id: 1
                            } as McBotFaceType)
                          : ({ ...value.config } as AIKittyType),
                      showModal: false
                    })
                  );
                  dispatch(
                    handleInitializeEditAppearance({
                      id: value.id,
                      bot_code: value.type as BotCode,
                      bot_type:
                        (value.type as BotCode) ===
                          BotTypeEnum.VIDEO_SCENARIO ||
                        (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                          ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                          : BotCategoryEnum.AI_BASED_CHATBOT,
                      title: value.title,
                      configs: value.config.settings
                    })
                  );
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                })
            : editAiKittyBot({
                id: wizard.id,
                ...(wizard.bot_config as AIKittyType),
                title: data.title
              })
                .unwrap()
                .then(() => {
                  toast.success(t(AlertsType.ISKBKO004));
                  dispatch(
                    ChatbotRTKService.util.invalidateTags([
                      "Package",
                      "GetAllScenarios",
                      "GetAssistantDetail"
                    ])
                  );
                  goToTitle();
                  setClonedData(wizard);
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                });
        }
      }
  };

  useEffect(() => {
    setClonedData(wizard);
  }, []);

  useEffect(() => {
    if (ChatbotEditContextValue?.value) {
      if (ChatbotEditContextValue?.value.assistantType)
        formBuilder.setValue(
          "assistant_type",
          ChatbotEditContextValue?.value.assistantType
        );

      if (ChatbotEditContextValue?.value.templateId)
        formBuilder.setValue(
          "template_id",
          ChatbotEditContextValue?.value.templateId
        );

      if (ChatbotEditContextValue?.value.title)
        formBuilder.setValue("title", ChatbotEditContextValue?.value.title);

      setIsLoading(ChatbotEditContextValue?.value.isLoading);
    } else {
      setIsLoading(true);
    }
  }, [ChatbotEditContextValue?.value]);

  useEffect(() => {
    return () => {
      formBuilder.reset();

      setWizardStep(1);

      dispatch(handleResetWizard());
    };
  }, []);

  const reScan = () => {
    reScanBot(wizard.id)
      .unwrap()
      .then(() => {
        toast.success(t("BusinessPartner/Payout/successEdit"));

        dispatch(
          ChatbotRTKService.util.invalidateTags([
            "Package",
            "GetAllScenarios",
            "GetAssistantDetail"
          ])
        );
        setClonedData(wizard);
      })
      .catch((err) => {
        toast.error(err.data?.message || err.message);
      });
  };
  if (isLoading) {
    return <LoadingComponent />;
  }
  return (
    <>
      <div className="lg:tw-p-6 tw-p-2 tw-bg-white tw-rounded-2xl tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] tw-flex-col  ">
        <form onSubmit={formBuilder.handleSubmit(onSubmit)}>
          <BotEditChatbotName formBuilder={formBuilder} />
          <hr className="tw-w-full" />
          {(wizard.bot_code == BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
            wizard.bot_code == BotTypeEnum.AI_READY_TO_USE ||
            wizard.bot_code == BotTypeEnum.HUMAN_CLONE) && (
            <>
              <AiAdditionalInfoEdit formBuilder={formBuilder} />
              <hr className="tw-w-full" />
            </>
          )}
          {wizard.bot_code == BotTypeEnum.HUMAN_CLONE && (
            <>
              <Form.Group className="tw-mb-4 tw-w-full ">
                <Form.Label>
                  <div className="tw-text-gray-800 tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-text-2xl">
                    {t("Wizard/CloneExtraDescription/key11")}
                  </div>
                </Form.Label>
                <Form.Select
                  value={
                    (wizard.bot_config as AIKittyType)?.cloneExtra
                      ?.template as string
                  }
                  className="tw-h-10 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-w-full"
                  {...formBuilder.register("template", {
                    onChange: (e) => {
                      dispatch(handleSetCloneTemplate(e.target.value));
                      switch (e.target.value) {
                        case "basic":
                          dispatch(
                            handleSetCloneExtra({
                              description: {
                                name: "",
                                profession: "",
                                bio: ""
                              },
                              videoBotId: "",
                              externalLinks: []
                            })
                          );
                          break;
                        case "clonePlusDescription":
                          dispatch(
                            handleSetCloneExtra({
                              videoBotId: "",
                              externalLinks: []
                            })
                          );
                          break;
                        case "clonePlusVideo":
                          dispatch(
                            handleSetCloneExtra({
                              description: {
                                name: "",
                                profession: "",
                                bio: ""
                              },
                              externalLinks: []
                            })
                          );
                          break;
                      }
                    }
                  })}
                >
                  <option value="basic">
                    {t("Wizard/CloneExtraDescription/key14")}
                  </option>
                  <option value="clonePlusDescription">
                    {t("Wizard/CloneExtraDescription/key12")}
                  </option>
                  <option value="clonePlusVideo">
                    {t("Wizard/CloneExtraDescription/key13")}
                  </option>
                </Form.Select>
              </Form.Group>

              {wizard.bot_code == BotTypeEnum.HUMAN_CLONE &&
                (wizard.bot_config as AIKittyType)?.cloneExtra?.template ==
                  "clonePlusDescription" && (
                  <>
                    <CloneExtraDescriptionEdit formBuilder={formBuilder} />
                    <hr className="tw-w-full" />
                  </>
                )}
              {wizard.bot_code == BotTypeEnum.HUMAN_CLONE &&
                (wizard.bot_config as AIKittyType)?.cloneExtra?.template ==
                  "clonePlusVideo" && (
                  <>
                    <CloneExtraVideoEdit formBuilder={formBuilder} />
                    <hr className="tw-w-full" />
                  </>
                )}
            </>
          )}

          {![BotTypeEnum.TEXT_SCENARIO, BotTypeEnum.VIDEO_SCENARIO].includes(
            wizard.bot_code as BotCode
          ) && (
            <>
              <ReactionSpeedEdit formBuilder={formBuilder} />
              <hr className="tw-w-full" />
            </>
          )}

          {wizard.bot_code == BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY && (
            <>
              <BotEditAgentWelcomingName formBuilder={formBuilder} />
              <hr className="tw-w-full" />
              <PlugAndPlayDomainEdit formBuilder={formBuilder} />
              <hr className="tw-w-full" />
              <PlugAndPlayDomainLinksEdit formBuilder={formBuilder} />
              <hr className="tw-w-full" />
              <PlugAndPlayAutoUpdateEdit formBuilder={formBuilder} />
              <hr className="tw-w-full" />
            </>
          )}

          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center sm:tw-items-end tw-w-full tw-justify-center sm:tw-justify-end tw-gap-3">
            {wizard.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY && (
              <LouiseButton
                variant="secondary"
                type="button"
                onClick={() => reScan()}
                disabled={reScanotResults.isLoading}
                className="tw-rounded-full tw-w-full sm:tw-w-auto"
              >
                {reScanotResults.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <BiRefresh className="tw-text-lg tw-mr-2" />
                    {t("Wizard/PlugAndPlayInfos/key21")}
                  </>
                )}
              </LouiseButton>
            )}

            <LouiseButton
              variant="lightBlue"
              type="submit"
              disabled={isApiInLoadingStatus || !formBuilder.formState.isDirty}
              className="tw-rounded-full tw-w-full sm:tw-w-auto"
            >
              {isApiInLoadingStatus && (
                <Spinner size="sm" className="tw-mr-2" />
              )}
              {t("Wizard/general/key7")}
            </LouiseButton>
          </div>
        </form>
      </div>

      <BotSessionLimit />
    </>
  );
}

export default BotEditConfiguration;
